import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DiscoveryCard from './DiscoveryCard';

/**
 * Horizontal scroll carousel for discovery sections.
 * - Mobile: free scroll with snap, 1.3 cards visible
 * - Desktop: 4 cards visible, arrows on hover
 * - Hides itself when <2 cards
 * - Emits a one-shot impression event on mount via sendBeacon
 */
export default function DiscoveryCarousel({
  id,
  title,
  subtitle,
  negocios = [],
  origemId,
  tipo,
  modo = null,
  municipioId,
  loading = false,
  showRecommendedBadge = false,
  showEmAltaBadge = false,
  onAuthRequired,
}) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Impression beacon (fire once when carousel has items)
  useEffect(() => {
    if (!negocios || negocios.length < 2 || !origemId) return;
    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/analytics/recommendation-impression`;
      const payload = {
        negocio_origem_id: origemId,
        negocio_destino_ids: negocios.map(n => n.id),
        tipo,
        municipio_id: municipioId,
        session_id: (() => {
          try {
            let sid = localStorage.getItem('ec_session_id');
            if (!sid) {
              sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
              localStorage.setItem('ec_session_id', sid);
            }
            return sid;
          } catch { return null; }
        })(),
        timestamp: new Date().toISOString(),
      };
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      }
    } catch {/* silent */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origemId, tipo, negocios.length]);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [negocios.length]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const w = el.clientWidth * 0.8;
    el.scrollBy({ left: dir * w, behavior: 'smooth' });
  };

  // Loading skeleton
  if (loading) {
    return (
      <section id={id} className="my-8" data-testid={`carousel-skeleton-${tipo}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-5 bg-amber-100 rounded w-64 mb-4 animate-pulse" />
          <div className="grid grid-flow-col auto-cols-[minmax(220px,_1fr)] gap-3 overflow-hidden">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden">
                <div className="aspect-[4/3] bg-amber-50 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-amber-50 rounded animate-pulse" />
                  <div className="h-3 bg-amber-50 rounded w-2/3 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!negocios || negocios.length < 2) return null;

  return (
    <section id={id} className="my-8 group/carousel" data-testid={`carousel-${tipo}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-[var(--text-primary)]" data-testid={`carousel-title-${tipo}`}>{title}</h2>
            {subtitle && <p className="text-xs text-[var(--text-secondary)] mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="relative">
          {/* Desktop arrows (hover-revealed) */}
          {canLeft && (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Voltar"
              data-testid={`carousel-left-${tipo}`}
              className="hidden lg:flex absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[var(--border)] shadow-md items-center justify-center z-10 opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:scale-105"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {canRight && (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Avançar"
              data-testid={`carousel-right-${tipo}`}
              className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[var(--border)] shadow-md items-center justify-center z-10 opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:scale-105"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {/* Scrollable rail */}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mb-2 scrollbar-thin"
            style={{ scrollbarWidth: 'thin' }}
          >
            {negocios.map(b => (
              <div
                key={b.id}
                className="snap-start flex-shrink-0 w-[78%] sm:w-[40%] lg:w-[calc((100%_-_2.25rem)/4)]"
              >
                <DiscoveryCard
                  business={b}
                  origemId={origemId}
                  tipo={tipo}
                  modo={modo}
                  municipioId={municipioId}
                  showRecommendedBadge={showRecommendedBadge}
                  showEmAltaBadge={showEmAltaBadge}
                  onAuthRequired={onAuthRequired}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
